import { Button, ButtonContainer, Card, ContentSection, Layout } from '@vwfs-bronson/bronson-react';
import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { SCP_API_KEY, SCP_WIDGET_BASE_URL, SCP_WIDGET_URL } from '../../../config';
import { validateBetterBePayload } from '../../../services/betterBe/betterBePayload.validator';
import { useNavigate } from '../../../services/routing';
import { sessionStorageService } from '../../../services/storage/SessionStorageService';
import { WizardPagesEnum } from '../newQuotation.slice';
import { newCarConfiguratorPageSlice } from './newCarConfiguratorPage.slice';

const SCP_WIDGET_ELEMENT_ID = 'scp-widget';
const SCP_SESSION_STORAGE_KEY = 'scpConfiguration';
const SCP_SCRIPT_ID = 'scp-widget-script';
const SCP_CALLBACK_HASH = '#scp-configured';

declare global {
  interface Window {
    BLS?: {
      SCP?: {
        startWidget: (config: Record<string, string>) => void;
      };
    };
  }
}

const getCallbackUrl = () => {
  const url = new URL(window.location.href);
  url.hash = SCP_CALLBACK_HASH;
  return url.toString();
};

const loadTheme = async () => {
  const theme = await import(`../../../config/betterbe/${process.env.REACT_APP_BRAND_TYPE || 'vwfs'}`).then(
    (module) => module.default
  );
  return theme;
};

export const NewCarConfiguratorPage: FunctionComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isConfigurationComplete, setIsConfigurationComplete] = useState(false);
  const dispatch = useDispatch();

  const initWidget = useCallback(async () => {
    if (!window.BLS?.SCP?.startWidget) return;

    const theme = await loadTheme();

    window.BLS.SCP.startWidget({
      ApiKey: SCP_API_KEY,
      WidgetBaseUrl: SCP_WIDGET_BASE_URL,
      WidgetElementId: SCP_WIDGET_ELEMENT_ID,
      ConfigurationFinishedCallback: getCallbackUrl(),
      ResultSessionStorageKey: SCP_SESSION_STORAGE_KEY,
      SearchFilters: 'equipment.jato111+=[AUDI,CUPRA,SEAT,SKODA,VOLKSWAGEN,PORSCHE]',
      Theme: theme,
    });
  }, []);

  // Widget normally rely on url callback, as we are in React SPA, callback is handled by hash control,
  // without page reload
  // to avoid react-router complains about unknown route, hash is captured in popstate event
  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      if (window.location.hash === SCP_CALLBACK_HASH) {
        e.stopImmediatePropagation();

        const result = sessionStorageService.get('scpWidget');
        if (result) {
          setIsConfigurationComplete(true);
        }

        // eslint-disable-next-line no-restricted-globals
        history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    };

    window.addEventListener('popstate', handlePopState, true);

    return () => {
      window.removeEventListener('popstate', handlePopState, true);
    };
  }, []);

  useEffect(() => {
    if (!SCP_WIDGET_URL) return;

    const existingScript = document.getElementById(SCP_SCRIPT_ID);
    if (existingScript) {
      initWidget();
      return;
    }

    const script = document.createElement('script');
    script.id = SCP_SCRIPT_ID;
    script.src = SCP_WIDGET_URL;
    script.defer = true;
    script.onload = () => {
      initWidget();
    };
    document.body.appendChild(script);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleContinue = () => {
    const result = sessionStorageService.get('scpWidget');

    if (result) {
      validateBetterBePayload(result);
      dispatch(newCarConfiguratorPageSlice.actions.setConfigurationResult(result));
    }

    navigate.newQuote(`/${WizardPagesEnum.financialProduct}`);
  };

  const handleBack = () => {
    navigate.newQuote(`/${WizardPagesEnum.quoteTypeSelection}`);
  };

  return (
    <>
      <ContentSection>
        <Card title={undefined}>
          <div id={SCP_WIDGET_ELEMENT_ID} />
        </Card>
      </ContentSection>

      <ContentSection pageWrapSize="small">
        <Layout center>
          <ButtonContainer center>
            <Layout.Item default="1/3">
              <Button type="button" full testId="button_back" secondary onClick={handleBack}>
                {t('newCarConfiguratorPage:navigation.back')}
              </Button>
            </Layout.Item>
            <Layout.Item default="1/3">
              <Button
                type="button"
                full
                testId="button_continue"
                disabled={!isConfigurationComplete}
                onClick={handleContinue}
              >
                {t('newCarConfiguratorPage:navigation.continue')}
              </Button>
            </Layout.Item>
          </ButtonContainer>
        </Layout>
      </ContentSection>
    </>
  );
};
